/**
 * The length of a campaign day. Every campaign deadline counts in these days. That covers the lead
 * time before the start, the acceptance lead time, the longest window, Day 3, Day 4, the review,
 * Day 8 and the settlement window from Day 5 to Day 10 (specification §11).
 *
 * Campaign days are real days of 86,400 seconds. On a test network, staging may shorten them so
 * that a whole campaign runs in about an hour (`CAMPAIGN_DAY_SECONDS`, docs/deployment.md). The
 * escrow must then be a test build whose days have the same length, because the contract counts
 * its own settlement window. Each process sets the length once when it starts, before it computes
 * any campaign time.
 *
 * Limits that come from outside the campaign stay in real days (`DAY_MS`). The 30 days of account
 * history the fraud checks read and the calendar dates YouTube reports by are examples. That is
 * why YouTube campaigns need real days.
 */

/** A real day in milliseconds. */
export const DAY_MS = 86_400_000;

/** The shortest campaign day a test network may use, in seconds. */
export const MIN_CAMPAIGN_DAY_SECONDS = 60;

/** The longest campaign day, in seconds: a real day. */
export const MAX_CAMPAIGN_DAY_SECONDS = 86_400;

let dayMs = DAY_MS;

/**
 * Sets the length of a campaign day for this process.
 *
 * @throws When `seconds` is not a whole number from 60 to 86,400.
 */
export function setCampaignDaySeconds(seconds: number): void {
  if (
    !Number.isInteger(seconds) ||
    seconds < MIN_CAMPAIGN_DAY_SECONDS ||
    seconds > MAX_CAMPAIGN_DAY_SECONDS
  ) {
    throw new Error(
      `A campaign day lasts from ${MIN_CAMPAIGN_DAY_SECONDS} to ${MAX_CAMPAIGN_DAY_SECONDS} seconds, not ${seconds}.`,
    );
  }
  dayMs = seconds * 1000;
}

/** The length of a campaign day in milliseconds. */
export function campaignDayMs(): number {
  return dayMs;
}

/** Whether campaign days are shorter than real days, as on a test network set up for quick runs. */
export function hasShortDays(): boolean {
  return dayMs < DAY_MS;
}

/**
 * `n` campaign days as people read them. With real days that is "3 days". With short days it is
 * the time they really take, such as "15 minutes".
 */
export function formatCampaignDays(n: number): string {
  if (!hasShortDays()) {
    return `${n} ${n === 1 ? "day" : "days"}`;
  }
  const seconds = (n * dayMs) / 1000;
  if (seconds % 3600 === 0) {
    return plural(seconds / 3600, "hour");
  }
  if (seconds % 60 === 0) {
    return plural(seconds / 60, "minute");
  }
  return plural(seconds, "second");
}

function plural(count: number, unit: string): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? unit : `${unit}s`}`;
}
